import { useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useAuthStore } from '../../store/authStore';
import PhoneWithCountryField from '../shared/PhoneWithCountryField';
import { isValidFullPhone, normPhoneDigits } from '../../utils/phoneCountryCodes';

const MIN_LEN = 8;

const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,.35)' },
  },
};

function PasswordRule({ ok, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {ok ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#4ade80' }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,.35)' }} />
      )}
      <Typography variant="caption" sx={{ color: ok ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.5)' }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function CatalogInterestModal({
  open,
  onClose,
  onSuccess,
  productPreview,
}) {
  const [mode, setMode] = useState('register');
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const register = useAuthStore((s) => s.register);
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);

  const pwdRules = useMemo(
    () => ({
      length: password.length >= MIN_LEN,
      letter: /[a-zA-Z]/.test(password),
      number: /[0-9]/.test(password),
    }),
    [password]
  );

  const pwdScore = useMemo(() => {
    let n = 0;
    if (pwdRules.length) n += 34;
    if (pwdRules.letter) n += 33;
    if (pwdRules.number) n += 33;
    return n;
  }, [pwdRules]);

  const pwdValid = pwdRules.length && pwdRules.letter && pwdRules.number;

  const handleClose = () => {
    setError('');
    setInfo('');
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    const mail = email.trim().toLowerCase();
    if (!mail) {
      setError('El correo es obligatorio');
      return;
    }
    if (!password) {
      setError('La contraseña es obligatoria');
      return;
    }
    if (mode === 'register' && !pwdValid) {
      setError('Usa una contraseña más segura');
      return;
    }
    if (mode === 'register') {
      if (!nombre.trim()) {
        setError('El nombre es obligatorio');
        return;
      }
      if (!apellido.trim()) {
        setError('El apellido es obligatorio');
        return;
      }
      if (!isValidFullPhone(whatsapp)) {
        setPhoneError(true);
        setError('Ingresa tu WhatsApp con código de país (ej. Bolivia +591)');
        return;
      }
    }
    setPhoneError(false);

    const result =
      mode === 'register'
        ? await register({
            email: mail,
            password,
            nombre: nombre.trim(),
            apellido: apellido.trim(),
            whatsapp: normPhoneDigits(whatsapp),
          })
        : await login(mail, password);

    if (!result.success) {
      setError(result.error || 'No se pudo completar');
      return;
    }
    if (result.pendingVerification) {
      setMode('login');
      setError('');
      setInfo(result.message || 'Revisa tu correo y confirma el enlace para activar tu cuenta.');
      return;
    }

    handleClose();
    await onSuccess?.();
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 420 },
            maxWidth: 440,
            bgcolor: '#141820',
            color: '#f3f4f6',
            borderRadius: 3,
            boxShadow: '0 24px 64px rgba(0,0,0,.45)',
            border: '1px solid rgba(255,255,255,.08)',
            p: { xs: 2.5, sm: 3 },
            outline: 'none',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
            <Box sx={{ pr: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                Guarda los productos que te interesan
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: 'rgba(255,255,255,.65)' }}>
                Usa la misma cuenta de Devssoft que en el login general. Si ya iniciaste sesión,
                cierra este cuadro y vuelve a tocar la estrella.
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: 'rgba(255,255,255,.6)', mt: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {productPreview?.nombre && (
            <Box
              sx={{
                mb: 2,
                py: 1,
                px: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,.06)',
                border: '1px solid rgba(255,255,255,.08)',
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.5)' }}>
                Producto seleccionado
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {productPreview.nombre}
              </Typography>
            </Box>
          )}

          {info && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {info}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={1.5}>
            <TextField
              label="Correo electrónico"
              type="email"
              required
              fullWidth
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              sx={darkFieldSx}
            />
            {mode === 'register' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  autoComplete="given-name"
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
                  sx={darkFieldSx}
                />
                <TextField
                  label="Apellido"
                  required
                  fullWidth
                  size="small"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  autoComplete="family-name"
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
                  sx={darkFieldSx}
                />
              </Stack>
            )}
            {mode === 'register' && (
              <PhoneWithCountryField
                label="WhatsApp"
                required
                theme="dark"
                value={whatsapp}
                onChange={(v) => {
                  setWhatsapp(v);
                  setPhoneError(false);
                }}
                error={phoneError}
                errorText="Incluye código de país y al menos 7 dígitos del número"
                helperText="Obligatorio para que la tienda pueda contactarte. Por defecto Bolivia (+591)."
              />
            )}
            <TextField
              label="Contraseña"
              type="password"
              required
              fullWidth
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              sx={darkFieldSx}
            />
            {mode === 'register' && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={pwdScore}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: 'rgba(255,255,255,.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: pwdValid ? '#4ade80' : '#f59e0b',
                      borderRadius: 2,
                    },
                  }}
                />
                <PasswordRule ok={pwdRules.length} label={`Mínimo ${MIN_LEN} caracteres`} />
                <PasswordRule ok={pwdRules.letter} label="Incluye letras" />
                <PasswordRule ok={pwdRules.number} label="Incluye números" />
              </Box>
            )}
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2.5,
              py: 1.1,
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: '#f59e0b',
              color: '#111',
              '&:hover': { bgcolor: '#d97706' },
            }}
          >
            {loading
              ? 'Espere…'
              : mode === 'register'
                ? 'Crear cuenta y guardar'
                : 'Iniciar sesión y guardar'}
          </Button>

          <Button
            fullWidth
            size="small"
            onClick={() => {
              setMode(mode === 'register' ? 'login' : 'register');
              setError('');
              setInfo('');
              setPhoneError(false);
            }}
            sx={{ mt: 1.25, color: 'rgba(255,255,255,.75)', textTransform: 'none' }}
          >
            {mode === 'register'
              ? '¿Ya tienes cuenta? Inicia sesión'
              : '¿No tienes cuenta? Crear cuenta'}
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
}
